import { ButtonProps } from 'components/Button';
import { CenteredImageWithTextProps } from 'components/CenteredImageWithText';
import { HtmlContentProps } from 'components/HtmlContent';
import { IconProps } from 'components/Icon';
import { LabelOnTopProps } from 'components/LabelOnTop';
import { PillProps } from 'components/Pill';
import { SectionHeaderProps } from 'components/SectionHeader';
import { SummaryProps } from 'components/Summary';
import { TextLinkProps } from 'components/TextAsLink';
import { TitleProps } from 'components/Title';
import { TitleAsLinkProps } from 'components/TitleAsLink';
import {
  RetreivalFilterTypes,
  RetrievalMaterialKeys,
} from 'redux/Retrieval/typings';

type Features = {
  hasSearch: boolean;
  hasChat: boolean;
};

type Search = {
  display: {
    searchSummary: boolean;
    relatedRequestThemes: boolean;
    searchResultPreview: boolean;
  };
  filters: SearchFilter[];
  searchSummary: SearchSummary;
  resultCardLayout: ResultLayout;
  resultPreviewLayout: ResultLayout;
  noResultPreviewLayout: ResultLayout;
};

type Chat = object;

export type SearchFilter = {
  id: string;
  label: string;
  filterType: RetreivalFilterTypes; // 'dateRange' | 'list' | 'switch';
  sourceDataKey: string;
};

type SearchSummary = {
  prompt: string;
};

type WorkspaceConfigurationId = string;

// should map to the component names in the dynamic folder
export enum DynamicComponentNames {
  TitleAsLink = 'TitleAsLink',
  LabelOnTop = 'LabelOnTop',
  Summary = 'Summary',
  AttachmentIcon = 'AttachmentIcon',
  DocumentIcon = 'DocumentIcon',
  DownloadIcon = 'DownloadIcon',
  HtmlContent = 'HtmlContent',
  Pill = 'Pill',
  SectionHeader = 'SectionHeader',
  TextAslink = 'TextAslink',
  CenteredImageWithText = 'CenteredImageWithText',
  EyeIcon = 'EyeIcon',
  CloseIcon = 'CloseIcon',
  IllustrationIcon = 'IllustrationIcon',
  Divider = 'Divider',
  Title = 'Title',
  Button = 'Button',
}

export type Origin = 'resultCard' | 'previewLayout';

export type Column = {
  id: string;
  size: number;
  requiredComponentRenderParams: RequiredRenderParam[];
  componentName: DynamicComponentNames;
  rows?: Row[];
};

export type RequiredRenderParam = DynamicRenderParam | StaticRenderParam;

export type DynamicRenderParam = {
  paramName: ParamName;
  paramType: 'dynamic';
  paramDynamicLookupKey: RetrievalMaterialKeys;
  paramStaticValue?: never;
  paramPostfix?: string;
  paramFormat?: 'DD/MM/YYYY' | 'MMM DD,YYYY';
};

export type StaticRenderParam = {
  paramName: ParamName;
  paramType: 'static';
  paramStaticValue: string;
  paramPostfix?: never;
  paramDynamicLookupKey?: never;
  paramFormat?: never;
};

export type ParamName =
  | keyof TitleAsLinkProps
  | keyof LabelOnTopProps
  | keyof SummaryProps
  | keyof IconProps
  | keyof PillProps
  | keyof TextLinkProps
  | keyof SectionHeaderProps
  | keyof CenteredImageWithTextProps
  | keyof HtmlContentProps
  | keyof TitleProps
  | keyof ButtonProps;

export type Row = {
  id: string;
  columns: Column[];
};

export type ResultLayout = {
  rows: Row[];
};

export type WorkspaceConfiguration = {
  id: WorkspaceConfigurationId;
  workspaceName: string;
  features: Features;
  search: Search;
  chat: Chat;
};

export enum CustomIconNames {
  AttachFile = 'attach_file',
  Download = 'download',
  ContentCopy = 'content_copy',
  Eye = 'eye',
  Close = 'close',
  Illustartion = 'illustration',
}

export type ComponentNameToPropsMap = {
  [DynamicComponentNames.TitleAsLink]: (keyof TitleAsLinkProps)[];
  [DynamicComponentNames.LabelOnTop]: (keyof LabelOnTopProps)[];
  [DynamicComponentNames.Summary]: (keyof SummaryProps)[];
  [DynamicComponentNames.AttachmentIcon]: (keyof IconProps)[];
  [DynamicComponentNames.DocumentIcon]: (keyof IconProps)[];
  [DynamicComponentNames.EyeIcon]: (keyof IconProps)[];
  [DynamicComponentNames.CloseIcon]: (keyof IconProps)[];
  [DynamicComponentNames.IllustrationIcon]: (keyof IconProps)[];
  [DynamicComponentNames.DownloadIcon]: (keyof IconProps)[];
  [DynamicComponentNames.HtmlContent]: (keyof HtmlContentProps)[];
  [DynamicComponentNames.Pill]: (keyof PillProps)[];
  [DynamicComponentNames.SectionHeader]: (keyof SectionHeaderProps)[];
  [DynamicComponentNames.TextAslink]: (keyof TextLinkProps)[];
  [DynamicComponentNames.CenteredImageWithText]: (keyof CenteredImageWithTextProps)[];
  [DynamicComponentNames.Divider]: () => [];
  [DynamicComponentNames.Title]: (keyof TitleProps)[];
  [DynamicComponentNames.Button]: (keyof ButtonProps)[];
};
